import { api } from '@/shared/lib/api';

export interface Demand {
  id: string;
  notionPageId: string;
  title: string;
  responsibleName: string;
  type: string | null;
  currentStatus: string;
  isActive: boolean;
  totalActiveDurationMs: number;
  cyclesCount: number;
  firstStartedAt: string | null;
  lastEndedAt: string | null;
  lastEventAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface DemandCycle {
  id: string;
  demandId: string;
  responsibleName: string;
  startedAt: string;
  endedAt: string | null;
  durationMs: number | null;
  startedFromStatus: string | null;
  endedToStatus: string | null;
  createdAt: string;
}

export interface DemandWithCycles extends Demand {
  cycles: DemandCycle[];
}

export interface ActiveDemand extends Demand {
  openCycle: DemandCycle | null;
}

export interface ResponsibleMetric {
  responsibleName: string;
  cyclesCount: number;
  totalDurationMs: number;
  averageDurationMs: number;
}

export interface TypeMetric {
  type: string;
  cyclesCount: number;
  totalDurationMs: number;
  averageDurationMs: number;
}

export interface WorkloadItem {
  responsibleName: string;
  activeCount: number;
  oldestElapsedMs: number;
  stuckCount: number;
}

export interface ThroughputPoint {
  date: string; // YYYY-MM-DD UTC
  closedCount: number;
  averageCycleMs: number;
}

export interface DemandsOverview {
  /** Ciclos abertos agora. */
  activeCount: number;
  /** Soma do tempo já corrido em todos os ciclos abertos. */
  activeElapsedMs: number;
  /** Nº de ciclos abertos há mais que `stuckThresholdMs`. */
  stuckCount: number;
  stuckThresholdMs: number;
  /** Ciclos fechados no período (throughput). */
  closedInPeriod: number;
  closedInPreviousPeriod: number;
  /** Tempo médio de ciclo fechado no período. */
  averageCycleMs: number;
  previousAverageCycleMs: number;
  /** % de demandas com >1 ciclo fechado no período (retrabalho). Entre 0 e 1. */
  reworkRate: number;
  previousReworkRate: number;
  /** Nº total de demandas distintas com ciclos fechados no período. */
  demandsInPeriod: number;
  /** Nº de demandas com >1 ciclo fechado no período. */
  demandsWithReworkInPeriod: number;
  /** % de ciclos fechados dentro do SLA do tipo (0-1). Considera só ciclos com SLA configurado. */
  slaComplianceRate: number;
  previousSlaComplianceRate: number;
  /** Ciclos que estouraram o SLA no período. */
  slaBreachCount: number;
  /** Ciclos fechados no período cujo tipo tinha SLA configurado (denominador). */
  slaCoveredCycles: number;
  /** Ciclos ABERTOS agora que já estouraram o SLA do seu tipo. */
  openCyclesBreachingSLA: number;
  /** Quantidade de SLAs configurados (enabled) — se 0, KPI de SLA é neutro. */
  slasConfigured: number;
  period: {
    from: string;
    to: string;
    previousFrom: string;
    previousTo: string;
  };
}

export interface SLAConfig {
  id: string;
  type: string;
  targetMs: number;
  warningMs: number | null;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertSLAInput {
  type: string;
  targetHours: number;
  warningHours?: number;
  enabled?: boolean;
}

export interface DemandsFacets {
  types: string[];
  statuses: string[];
  responsibles: string[];
}

export interface DistributionBucket {
  label: string;
  minMs: number;
  maxMs: number | null;
  count: number;
}

export interface DistributionResult {
  count: number;
  minMs: number;
  maxMs: number;
  avgMs: number;
  p50Ms: number;
  p90Ms: number;
  p99Ms: number;
  buckets: DistributionBucket[];
  filters: {
    from: string | null;
    to: string | null;
    type: string | null;
    responsibleName: string | null;
  };
}

export interface DistributionFilters {
  from?: string;
  to?: string;
  type?: string;
  responsibleName?: string;
}

export interface OverviewFilters {
  from?: string;
  to?: string;
  stuckHours?: number;
}

export interface DemandListFilters {
  page?: number;
  limit?: number;
  status?: string;
  responsibleName?: string;
  type?: string;
  isActive?: boolean;
  hasRework?: boolean;
  search?: string;
}

export interface DemandListResponse {
  data: Demand[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  timestamp: string;
}

export const demandsAPI = {
  list: async (filters: DemandListFilters = {}): Promise<DemandListResponse> => {
    const params: Record<string, string | number | boolean> = {};
    if (filters.page) params.page = filters.page;
    if (filters.limit) params.limit = filters.limit;
    if (filters.status) params.status = filters.status;
    if (filters.responsibleName) params.responsibleName = filters.responsibleName;
    if (filters.type) params.type = filters.type;
    if (typeof filters.isActive === 'boolean') params.isActive = filters.isActive;
    if (typeof filters.hasRework === 'boolean') params.hasRework = filters.hasRework;
    if (filters.search) params.search = filters.search;
    const response = await api.get('/demands', { params });
    return response.data;
  },

  facets: async (): Promise<{ data: DemandsFacets; timestamp: string }> => {
    const response = await api.get('/demands/meta/facets');
    return response.data;
  },

  distribution: async (
    filters: DistributionFilters = {}
  ): Promise<{ data: DistributionResult; timestamp: string }> => {
    const params: Record<string, string> = {};
    if (filters.from) params.from = filters.from;
    if (filters.to) params.to = filters.to;
    if (filters.type) params.type = filters.type;
    if (filters.responsibleName) params.responsibleName = filters.responsibleName;
    const response = await api.get('/demands/metrics/distribution', { params });
    return response.data;
  },

  listSLAs: async (): Promise<{ data: SLAConfig[]; timestamp: string }> => {
    const response = await api.get('/demands/sla');
    return response.data;
  },

  upsertSLA: async (
    input: UpsertSLAInput
  ): Promise<{ data: SLAConfig; timestamp: string }> => {
    const response = await api.post('/demands/sla', input);
    return response.data;
  },

  deleteSLA: async (id: string): Promise<void> => {
    await api.delete(`/demands/sla/${id}`);
  },

  listActive: async (): Promise<{ data: ActiveDemand[] }> => {
    const response = await api.get('/demands/active');
    return response.data;
  },

  metricsByResponsible: async (
    filters: { from?: string; to?: string } = {}
  ): Promise<{ data: ResponsibleMetric[]; filters: { from: string | null; to: string | null } }> => {
    const response = await api.get('/demands/metrics/by-responsible', { params: filters });
    return response.data;
  },

  metricsByType: async (
    filters: { from?: string; to?: string } = {}
  ): Promise<{ data: TypeMetric[]; filters: { from: string | null; to: string | null } }> => {
    const response = await api.get('/demands/metrics/by-type', { params: filters });
    return response.data;
  },

  workload: async (
    filters: { stuckHours?: number } = {}
  ): Promise<{ data: WorkloadItem[]; stuckThresholdMs: number; timestamp: string }> => {
    const params: Record<string, number> = {};
    if (typeof filters.stuckHours === 'number') params.stuckHours = filters.stuckHours;
    const response = await api.get('/demands/metrics/workload', { params });
    return response.data;
  },

  throughput: async (
    filters: { from?: string; to?: string } = {}
  ): Promise<{
    data: ThroughputPoint[];
    period: { from: string; to: string };
    timestamp: string;
  }> => {
    const response = await api.get('/demands/metrics/throughput', { params: filters });
    return response.data;
  },

  overview: async (
    filters: OverviewFilters = {}
  ): Promise<{ data: DemandsOverview; timestamp: string }> => {
    const params: Record<string, string | number> = {};
    if (filters.from) params.from = filters.from;
    if (filters.to) params.to = filters.to;
    if (typeof filters.stuckHours === 'number') params.stuckHours = filters.stuckHours;
    const response = await api.get('/demands/metrics/overview', { params });
    return response.data;
  },

  getById: async (id: string): Promise<{ data: DemandWithCycles }> => {
    const response = await api.get(`/demands/${id}`);
    return response.data;
  },
};
